import React, { useState, useEffect } from "react";
import ChecklistActionItemsContainer from "../ActionItems/ChecklistActionItemsContainer";
import { NavigationalButtons } from "./CreateChecklistWizard";
import useAxios from "axios-hooks";
import { MessageBar, MessageBarType, Spinner, Text, Stack, Link, Panel, PanelType } from "@fluentui/react";
import BasicInfoForm from "../../ActionItems/BasicInfo/BasicInfoForm";
import useCreateActionItem from "../ActionItems/useCreateActionItem";

const extractErrorMessages = (error) => {
  const data = error?.response?.data;
  if (!data) return [error.message || "An unexpected error occurred."];
  if (Array.isArray(data.errors)) {
    return data.errors.map((e) => e.errorMessage || e.ErrorMessage || JSON.stringify(e));
  }
  if (data.errors && typeof data.errors === "object") {
    return Object.values(data.errors).flat();
  }
  if (data.title) return [data.title];
  if (typeof data === "string") return [data];
  return ["An unexpected error occurred."];
};

const ActionItemsStep = ({
  checklistId,
  prevOnClick,
  nextOnClick,
}) => {
  const [showCreateActionItemForm, setShowCreateActionItemForm] = useState(
    false
  );
  const [errorMessages, setErrorMessages] = useState(null);

  const [{ loading: putActionItemsLoading }, putActionItems] = useAxios(
    {
      url: `/checklist/${checklistId}/action-items`,
      method: "put",
    },
    { manual: true }
  );

  const [
    { data: checklistData, loading: loadingChecklist },
    getChecklistData,
  ] = useAxios(
    {
      url: `checklist/${checklistId}`,
      method: "get",
    },
    { manual: true }
  );

  const [actionItems, setActionItems] = useState(
    (checklistData && checklistData.actionItems) || []
  );

  const [
    createActionItem,
    creatingActionItem,
    allActionItemsData,
    loadingAllActionItems,
  ] = useCreateActionItem(setActionItems, checklistId, checklistData);

  useEffect(() => {
    if (!checklistData && !loadingChecklist) {
      getChecklistData();
    }
  }, [checklistData, getChecklistData, loadingChecklist]);

  if (loadingChecklist) {
    return <Spinner label="Getting Checklist Info" />;
  }
  return (
    <div style={{width:770}}>
      {errorMessages && (
        <MessageBar
          messageBarType={MessageBarType.error}
          isMultiline
          onDismiss={() => setErrorMessages(null)}
        >
          {errorMessages.map((msg, i) => (
            <div key={i}>{msg}</div>
          ))}
        </MessageBar>
      )}
      <ChecklistActionItemsContainer
        putActionItemsLoading={putActionItemsLoading}
        putActionItems={putActionItems}
        checklistId={checklistId}
        actionItems={actionItems}
        setActionItems={setActionItems}
        checklist={checklistData}
        setIsModalCreateNewModalOpen={() => {
          setShowCreateActionItemForm(true);
        }}
        actionItemsData={allActionItemsData}
        loadingActionItems={loadingAllActionItems}
        isWizard
      />
      <NavigationalButtons
        onNextHandler={async () => {
          setErrorMessages(null);
          try {
            await putActionItems({
              data: actionItems.map((actionItem) => ({
                actionItemID: actionItem.actionItemID,
                checklistID: checklistId,
                orderNumber: actionItem.orderNumber,
                 requirement:
                  actionItem.requirement ||
                  (checklistData?.completionRules === "Required Only" ? "Required" : "Not Required"),
              })),
              method: "put",
            });
            nextOnClick();
          } catch (e) {
            console.error(e);
            setErrorMessages(extractErrorMessages(e));
          }
        }}
        onNextProps={{ text: "Save and Continue" }}
        prevOnClick={prevOnClick}
      />
      <Panel
        isOpen={showCreateActionItemForm}
        onDismiss={() => setShowCreateActionItemForm(false)}
        type={PanelType.medium}
        headerText="Create an Action Item2"
        closeButtonAriaLabel="Close"
      >
        <BasicInfoForm
          isWizard
          postForm={createActionItem}
          submitting={creatingActionItem}
          onCreateSuccess={() => {
            // Close the panel - the action item list is automatically
            // refreshed by useCreateActionItem hook
            setShowCreateActionItemForm(false);
          }}
        />
      </Panel>
    </div>
  );
};

export default ActionItemsStep;
